// Store maestros (drivers, warehouses, clients, alerts). Hidratado desde
// la REST API cuando hay un user logueado; el seed sobrevive como
// fallback si la API no responde y para que los tests unitarios sigan
// corriendo sin red. Edits locales (mutations) viven en memoria +
// localStorage — los endpoints PATCH/POST llegan en M3.

import { create } from "zustand";
import { persist } from "zustand/middleware";
import { api } from "./api";
import {
  SEED_CLIENT_ADDRESSES,
  SEED_CLIENTS,
  SEED_NOTIFICATIONS,
  SEED_USERS,
  SEED_VEHICLES,
  SEED_WAREHOUSES,
} from "./seed";
import type {
  Client,
  ClientAddress,
  Notification,
  User,
  Vehicle,
  Warehouse,
} from "./schemaTypes";

type MastersState = {
  users: User[];
  warehouses: Warehouse[];
  vehicles: Vehicle[];
  clients: Client[];
  clientAddresses: ClientAddress[];
  notifications: Notification[];

  // --- hydration flags ---------------------------------------------------
  hydrating: boolean;
  hydrationError: string | null;
  hydratedAt: string | null;

  // --- mutations --------------------------------------------------------
  updateUser: (id: string, patch: Partial<User>) => void;
  updateWarehouse: (id: string, patch: Partial<Warehouse>) => void;
  updateClient: (id: string, patch: Partial<Client>) => void;
  markNotificationRead: (id: string) => void;
  markAllNotificationsRead: () => void;

  /** Carga desde la REST. Si falla, conserva los datos previos del seed
   *  o de la última carga exitosa (graceful degradation). */
  hydrate: (opts?: { targetUserID?: string }) => Promise<void>;

  /** Limpia el state al cerrar sesión — evita que un usuario vea data
   *  cacheada de otro hasta que hydrate() corra. Usa el seed como
   *  fallback offline (los tests dependen de eso). */
  clearForLogout: () => void;

  // --- reset to seed (debug helper) ------------------------------------
  resetToSeed: () => void;
};

const initialState = {
  users: SEED_USERS,
  warehouses: SEED_WAREHOUSES,
  vehicles: SEED_VEHICLES,
  clients: SEED_CLIENTS,
  clientAddresses: SEED_CLIENT_ADDRESSES,
  notifications: SEED_NOTIFICATIONS,
  hydrating: false,
  hydrationError: null as string | null,
  hydratedAt: null as string | null,
};

export const useMastersStore = create<MastersState>()(
  persist(
    (set) => ({
      ...initialState,

      // Optimistic update: aplicamos el patch en memoria de inmediato
      // para que la UI responda al instante; si la API falla, hacemos
      // rollback al estado previo y guardamos el error para que la
      // página lo muestre. La estrategia mejora la latencia percibida
      // pero el caller debe estar preparado para que el state "deshaga"
      // su edit segundos después si la red está caída.
      // Optimistic update: aplicamos el patch en memoria al instante y
      // disparamos el fetch en segundo plano. La estrategia mejora la
      // latencia percibida; si la API falla rollback al estado previo
      // y registramos el error. NO reescribimos el state con la respuesta
      // de la API en éxito — el backend puede normalizar campos pero los
      // que vinieron en el patch ya están aplicados y duplicar el
      // reemplazo agrega complejidad sin valor.
      updateUser: (id, patch) => {
        const prev = useMastersStore.getState().users.find((u) => u.id === id);
        set((s) => ({
          users: s.users.map((u) => (u.id === id ? { ...u, ...patch } : u)),
        }));
        api.updateUser(id, patch).catch((err) => {
          if (prev) {
            set((s) => ({
              users: s.users.map((u) => (u.id === id ? prev : u)),
            }));
          }
          set({ hydrationError: `updateUser failed: ${err.message}` });
        });
      },

      updateWarehouse: (id, patch) => {
        const prev = useMastersStore.getState().warehouses.find((w) => w.id === id);
        set((s) => ({
          warehouses: s.warehouses.map((w) =>
            w.id === id ? { ...w, ...patch } : w,
          ),
        }));
        api.updateWarehouse(id, patch).catch((err) => {
          if (prev) {
            set((s) => ({
              warehouses: s.warehouses.map((w) => (w.id === id ? prev : w)),
            }));
          }
          set({ hydrationError: `updateWarehouse failed: ${err.message}` });
        });
      },

      updateClient: (id, patch) => {
        const prev = useMastersStore.getState().clients.find((c) => c.id === id);
        set((s) => ({
          clients: s.clients.map((c) =>
            c.id === id ? { ...c, ...patch } : c,
          ),
        }));
        api.updateClient(id, patch).catch((err) => {
          if (prev) {
            set((s) => ({
              clients: s.clients.map((c) => (c.id === id ? prev : c)),
            }));
          }
          set({ hydrationError: `updateClient failed: ${err.message}` });
        });
      },

      markNotificationRead: (id) => {
        const target = useMastersStore.getState().notifications.find((n) => n.id === id);
        if (!target || target.read_at) return;
        const now = new Date().toISOString();
        set((s) => ({
          notifications: s.notifications.map((n) =>
            n.id === id ? { ...n, read_at: now } : n,
          ),
        }));
        // target_user_id sale del propio registro — la API lo necesita
        // como anti-IDOR check. Si la llamada falla, rollback.
        api.markNotificationRead(id, target.target_user_id).catch((err) => {
          set((s) => ({
            notifications: s.notifications.map((n) =>
              n.id === id ? { ...n, read_at: undefined } : n,
            ),
            hydrationError: `markRead failed: ${err.message}`,
          }));
        });
      },

      markAllNotificationsRead: () => {
        // Necesitamos un target_user_id concreto — agarramos el del
        // primer ítem no leído. Asumimos que todos pertenecen al user
        // logueado (la API ya filtra por target).
        const target = useMastersStore
          .getState()
          .notifications.find((n) => !n.read_at)?.target_user_id;
        if (!target) return;
        const before = useMastersStore.getState().notifications;
        const now = new Date().toISOString();
        set((s) => ({
          notifications: s.notifications.map((n) =>
            n.read_at ? n : { ...n, read_at: now },
          ),
        }));
        api.markAllNotificationsRead(target).catch((err) => {
          set({ notifications: before, hydrationError: `markAllRead failed: ${err.message}` });
        });
      },

      resetToSeed: () => set(initialState),

      clearForLogout: () => {
        // Reset al seed (mismo que resetToSeed) pero pensado para el
        // flujo de logout: borra cualquier dato hidratado de la API que
        // pertenezca al usuario que se está desconectando. La diferencia
        // semántica vs resetToSeed es intencional — quien lea el código
        // ve "logout" en el llamador y entiende el por qué.
        set({ ...initialState, hydrationError: null });
      },

      hydrate: async (opts) => {
        set({ hydrating: true, hydrationError: null });
        try {
          // Disparamos los listings en paralelo — el pool del backend
          // está tuneado para soportar varios round-trips simultáneos.
          // El listado de notifications necesita target_user_id; si no
          // tenemos sesión, lo saltamos.
          const [users, warehouses, vehicles, clients, notifications] =
            await Promise.all([
              api.listUsers().catch(() => null),
              api.listWarehouses().catch(() => null),
              api.listVehicles().catch(() => null),
              api.listClients().catch(() => null),
              opts?.targetUserID
                ? api.listNotifications(opts.targetUserID).catch(() => null)
                : Promise.resolve(null),
            ]);

          set((s) => ({
            users: users ?? s.users,
            warehouses: warehouses ?? s.warehouses,
            vehicles: vehicles ?? s.vehicles,
            clients: clients ?? s.clients,
            notifications: notifications ?? s.notifications,
            // clientAddresses se hidrata por demanda en el drawer del
            // cliente — no las cargamos masivamente al login.
            hydratedAt: new Date().toISOString(),
            hydrating: false,
          }));
        } catch (e) {
          // Fallback graceful: nos quedamos con los datos previos
          // (seed o última carga buena). Sólo registramos el error.
          set({
            hydrationError: e instanceof Error ? e.message : String(e),
            hydrating: false,
          });
        }
      },
    }),
    {
      name: "micelio-masters",
      version: 1,
    },
  ),
);

// --- Selectors --------------------------------------------------------
// Helpers para reutilizar derivaciones desde tests u otras capas. NO los
// pases directamente a `useMastersStore(selector)` cuando devuelven un
// array (filter/map/slice) — la referencia cambia cada llamada y zustand
// v5 hace getSnapshot por identidad, lo que dispara un loop "Maximum
// update depth exceeded". Patrón correcto en componentes:
//
//   const users = useMastersStore((s) => s.users);
//   const drivers = useMemo(() => users.filter(...), [users]);
//
// Los selectors que devuelven primitives (selectUnreadCount) sí son
// seguros como selector del hook.

export const selectDrivers = (s: MastersState) =>
  s.users.filter((u) => u.role === "driver");

export const selectVehicleForDriver = (
  s: MastersState,
  driverId: string,
): Vehicle | undefined => s.vehicles.find((v) => v.current_driver_user_id === driverId);

export const selectAddressesForClient = (
  s: MastersState,
  clientId: string,
): ClientAddress[] => s.clientAddresses.filter((a) => a.client_id === clientId);

export const selectUnreadCount = (s: MastersState): number =>
  s.notifications.filter((n) => !n.read_at).length;
